import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import {
  getMyStore,
  getStoreInventoryItems,
  createStoreInventoryItem,
  updateStoreInventoryItem,
  toggleActiveStoreInventoryItem,
  deleteStoreInventoryItem,
  registerInventoryMovement,
  getStoreInventoryMovements,
  StoreInventoryItem,
  StoreInventoryMovement,
  InventoryMovementType
} from '../../services/supabaseService';
import { PredefinedInventoryModal } from '../../components/shopkeeper/PredefinedInventoryModal';
import { PREDEFINED_INVENTORY_ITEMS } from '../../constants/predefinedInventoryItems';

const PRESET_CATEGORIES = [
  'Embalagens & Envios',
  'Premiações & Torneios',
  'Acessórios & Sleeves',
  'Material de Consumo',
  'Lanchonete & Bebidas',
  'Limpeza & Manutenção',
  'Escritório & Papelaria',
  'Outros'
];

const COMMON_UNITS = [
  { value: 'un', label: 'Unidade (un)' },
  { value: 'pct', label: 'Pacote (pct)' },
  { value: 'cx', label: 'Caixa (cx)' },
  { value: 'kg', label: 'Quilograma (kg)' },
  { value: 'g', label: 'Grama (g)' },
  { value: 'l', label: 'Litro (l)' },
  { value: 'ml', label: 'Mililitro (ml)' },
  { value: 'rolo', label: 'Rolo (rolo)' },
  { value: 'm', label: 'Metro (m)' },
  { value: 'par', label: 'Par (par)' }
];

const REASON_SUGGESTIONS: Record<InventoryMovementType, string[]> = {
  entry: ['Compra / Reposição', 'Recebimento de Fornecedor', 'Doação / Brinde', 'Devolução'],
  consumption: ['Torneio / Evento', 'Uso no Balcão', 'Embalagem de Pedido', 'Consumo da Equipe', 'Uso Operacional'],
  adjustment_in: ['Balanço / Inventário Periódico', 'Correção de Contagem', 'Item Localizado'],
  adjustment_out: ['Avaria / Dano', 'Produto Vencido', 'Perda / Extravio', 'Correção de Contagem']
};

export const ManageInventory: React.FC = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  // State
  const [store, setStore] = useState<any>(null);
  const [items, setItems] = useState<StoreInventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters & Sorting
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'low_stock' | 'out_of_stock' | 'normal' | 'inactive'>('all');
  const [sortBy, setSortBy] = useState<'name_asc' | 'qty_asc' | 'qty_desc' | 'cost_desc' | 'val_desc'>('name_asc');

  // Modals
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [isPredefinedModalOpen, setIsPredefinedModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<StoreInventoryItem | null>(null);
  const [itemFormData, setItemFormData] = useState({
    name: '',
    description: '',
    category: '',
    unit: 'un',
    quantity: '0',
    minimum_quantity: '',
    cost: '',
    active: true,
    initial_reason: 'Estoque inicial',
    initial_notes: ''
  });
  const [savingItem, setSavingItem] = useState(false);

  // Movement Modal
  const [isMovementModalOpen, setIsMovementModalOpen] = useState(false);
  const [movementItem, setMovementItem] = useState<StoreInventoryItem | null>(null);
  const [movementType, setMovementType] = useState<InventoryMovementType>('consumption');
  const [movementQuantity, setMovementQuantity] = useState<string>('1');
  const [movementReason, setMovementReason] = useState<string>('');
  const [movementNotes, setMovementNotes] = useState<string>('');
  const [savingMovement, setSavingMovement] = useState(false);

  // History Drawer / Modal
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historyItem, setHistoryItem] = useState<StoreInventoryItem | null>(null);
  const [movements, setMovements] = useState<StoreInventoryMovement[]>([]);
  const [loadingMovements, setLoadingMovements] = useState(false);

  // Load store and items
  const loadData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      const myStore = await getMyStore();
      if (!myStore) {
        setStore(null);
        setItems([]);
        return;
      }
      setStore(myStore);

      const inventoryItems = await getStoreInventoryItems(myStore.id, {
        statusFilter: 'all' // Trazemos todos para manipular filtros e contadores no frontend
      });
      setItems(inventoryItems);
    } catch (err: any) {
      console.error('Error loading inventory:', err);
      showNotification('Erro ao carregar o inventário da loja.', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadData();
  }, [loadData, user?.id]);

  // Derived Categories
  const existingCategories = useMemo(() => {
    const cats = new Set<string>();
    items.forEach(i => {
      if (i.category && i.category.trim()) cats.add(i.category.trim());
    });
    PRESET_CATEGORIES.forEach(c => cats.add(c));
    return Array.from(cats).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [items]);

  // Statistics
  const stats = useMemo(() => {
    const activeItems = items.filter(i => i.active);
    const lowStock = activeItems.filter(i => i.minimum_quantity !== null && i.quantity <= i.minimum_quantity && i.quantity > 0);
    const outOfStock = activeItems.filter(i => i.quantity <= 0);
    const totalVal = activeItems.reduce((acc, curr) => acc + (curr.quantity * (curr.cost || 0)), 0);

    return {
      total: activeItems.length,
      lowStock: lowStock.length,
      outOfStock: outOfStock.length,
      totalVal
    };
  }, [items]);

  // Filtered & Sorted items
  const filteredItems = useMemo(() => {
    let result = [...items];

    // Status filter
    if (statusFilter === 'inactive') {
      result = result.filter(i => !i.active);
    } else {
      result = result.filter(i => i.active);
      if (statusFilter === 'low_stock') {
        result = result.filter(i => i.minimum_quantity !== null && i.quantity <= i.minimum_quantity && i.quantity > 0);
      } else if (statusFilter === 'out_of_stock') {
        result = result.filter(i => i.quantity <= 0);
      } else if (statusFilter === 'normal') {
        result = result.filter(i => (i.minimum_quantity === null || i.quantity > i.minimum_quantity) && i.quantity > 0);
      }
    }

    // Category filter
    if (selectedCategory !== 'all') {
      result = result.filter(i => i.category === selectedCategory);
    }

    // Search query
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(i => 
        i.name.toLowerCase().includes(q) || 
        (i.description && i.description.toLowerCase().includes(q)) ||
        (i.category && i.category.toLowerCase().includes(q))
      );
    }

    // Sorting
    result.sort((a, b) => {
      if (sortBy === 'name_asc') return a.name.localeCompare(b.name, 'pt-BR');
      if (sortBy === 'qty_asc') return a.quantity - b.quantity;
      if (sortBy === 'qty_desc') return b.quantity - a.quantity;
      if (sortBy === 'cost_desc') return (b.cost || 0) - (a.cost || 0);
      if (sortBy === 'val_desc') return ((b.quantity * (b.cost || 0)) - (a.quantity * (a.cost || 0)));
      return 0;
    });

    return result;
  }, [items, statusFilter, selectedCategory, search, sortBy]);

  // Open Create Modal
  const handleOpenCreateModal = () => {
    setEditingItem(null);
    setItemFormData({
      name: '',
      description: '',
      category: '',
      unit: 'un',
      quantity: '0',
      minimum_quantity: '',
      cost: '',
      active: true,
      initial_reason: 'Estoque inicial',
      initial_notes: ''
    });
    setIsItemModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEditModal = (item: StoreInventoryItem) => {
    setEditingItem(item);
    setItemFormData({
      name: item.name,
      description: item.description || '',
      category: item.category || '',
      unit: item.unit || 'un',
      quantity: String(item.quantity),
      minimum_quantity: item.minimum_quantity !== null && item.minimum_quantity !== undefined ? String(item.minimum_quantity) : '',
      cost: item.cost !== null && item.cost !== undefined ? String(item.cost) : '',
      active: item.active,
      initial_reason: '',
      initial_notes: ''
    });
    setIsItemModalOpen(true);
  };

  // Save Item (Create or Update)
  const handleSaveItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!store?.id) return;

    if (!itemFormData.name.trim()) {
      showNotification('O nome do item é obrigatório.', 'error');
      return;
    }

    try {
      setSavingItem(true);

      if (editingItem) {
        // Atualiza campos cadastrais
        const updated = await updateStoreInventoryItem(editingItem.id, {
          name: itemFormData.name,
          description: itemFormData.description,
          category: itemFormData.category,
          unit: itemFormData.unit,
          minimum_quantity: itemFormData.minimum_quantity ? Number(itemFormData.minimum_quantity) : null,
          cost: itemFormData.cost ? Number(itemFormData.cost) : null,
          active: itemFormData.active
        });

        setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
        showNotification('Item atualizado com sucesso!', 'success');
      } else {
        // Cria novo item
        const created = await createStoreInventoryItem({
          store_id: store.id,
          name: itemFormData.name,
          description: itemFormData.description,
          category: itemFormData.category,
          unit: itemFormData.unit,
          quantity: itemFormData.quantity ? Number(itemFormData.quantity) : 0,
          minimum_quantity: itemFormData.minimum_quantity ? Number(itemFormData.minimum_quantity) : null,
          cost: itemFormData.cost ? Number(itemFormData.cost) : null,
          active: itemFormData.active,
          initial_reason: itemFormData.initial_reason,
          initial_notes: itemFormData.initial_notes
        });

        setItems(prev => [created, ...prev]);
        showNotification('Item cadastrado com sucesso!', 'success');
      }

      setIsItemModalOpen(false);
    } catch (err: any) {
      console.error('Error saving inventory item:', err);
      const msg = err.message?.includes('store_inventory_items_store_name_key')
        ? 'Já existe um item cadastrado com este nome nesta loja.'
        : (err.message || 'Erro ao salvar o item.');
      showNotification(msg, 'error');
    } finally {
      setSavingItem(false);
    }
  };

  // Open Movement Modal
  const handleOpenMovementModal = (item: StoreInventoryItem, initialType: InventoryMovementType = 'consumption') => {
    setMovementItem(item);
    setMovementType(initialType);
    setMovementQuantity('1');
    setMovementReason('');
    setMovementNotes('');
    setIsMovementModalOpen(true);
  };

  // Compute preview for movement
  const movementPreview = useMemo(() => {
    if (!movementItem) return null;
    const current = Number(movementItem.quantity) || 0;
    const qty = parseFloat(movementQuantity) || 0;

    let next = current;
    if (movementType === 'entry' || movementType === 'adjustment_in') {
      next = current + qty;
    } else {
      next = current - qty;
    }

    return {
      current,
      qty,
      next,
      isNegative: next < 0
    };
  }, [movementItem, movementType, movementQuantity]);

  // Execute Movement via RPC
  const handleExecuteMovement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!movementItem) return;

    const qty = parseFloat(movementQuantity);
    if (isNaN(qty) || qty <= 0) {
      showNotification('A quantidade da movimentação deve ser maior que zero.', 'error');
      return;
    }

    if ((movementType === 'consumption' || movementType === 'adjustment_out') && qty > movementItem.quantity) {
      showNotification(`Estoque insuficiente. Disponível: ${movementItem.quantity} ${movementItem.unit}`, 'error');
      return;
    }

    try {
      setSavingMovement(true);

      const updated = await registerInventoryMovement({
        p_item_id: movementItem.id,
        p_movement_type: movementType,
        p_quantity: qty,
        p_reason: movementReason || (movementType === 'entry' ? 'Entrada manual' : 'Consumo manual'),
        p_notes: movementNotes || null
      });

      // Atualiza o item localmente
      setItems(prev => prev.map(i => i.id === updated.id ? updated : i));

      const typeLabel = 
        movementType === 'entry' ? 'Entrada registrada' :
        movementType === 'consumption' ? 'Consumo registrado' :
        movementType === 'adjustment_in' ? 'Ajuste positivo aplicado' : 'Ajuste negativo aplicado';

      showNotification(`${typeLabel}! Novo saldo: ${updated.quantity} ${updated.unit}`, 'success');
      setIsMovementModalOpen(false);
    } catch (err: any) {
      console.error('Error executing inventory movement:', err);
      showNotification(err.message || 'Erro ao registrar movimentação.', 'error');
    } finally {
      setSavingMovement(false);
    }
  };

  // Open History
  const handleOpenHistory = async (item?: StoreInventoryItem) => {
    if (!store?.id) return;
    setHistoryItem(item || null);
    setIsHistoryModalOpen(true);
    setLoadingMovements(true);

    try {
      const data = await getStoreInventoryMovements(store.id, {
        itemId: item?.id,
        limit: 80
      });
      setMovements(data);
    } catch (err: any) {
      console.error('Error loading movements history:', err);
      showNotification('Erro ao carregar histórico de movimentações.', 'error');
    } finally {
      setLoadingMovements(false);
    }
  };

  // Toggle active status
  const handleToggleActive = async (item: StoreInventoryItem) => {
    const actionText = item.active ? 'desativar' : 'reativar';
    const confirm = await Swal.fire({
      title: `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} item?`,
      text: item.active 
        ? 'O item deixará de aparecer nas contagens ativas do almoxarifado.' 
        : 'O item voltará a ficar visível no inventário ativo.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: `Sim, ${actionText}`,
      cancelButtonText: 'Cancelar',
      confirmButtonColor: item.active ? '#ef4444' : '#10b981',
      background: '#0f172a',
      color: '#f8fafc'
    });

    if (!confirm.isConfirmed) return;

    try {
      await toggleActiveStoreInventoryItem(item.id, !item.active);
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, active: !item.active } : i));
      showNotification(`Item ${item.active ? 'desativado' : 'reativado'} com sucesso!`, 'success');
    } catch (err: any) {
      console.error('Error toggling active:', err);
      showNotification('Erro ao alterar status do item.', 'error');
    }
  };

  // Delete item physically
  const handleDeleteItem = async (item: StoreInventoryItem) => {
    const confirm = await Swal.fire({
      title: 'Excluir definitivamente?',
      html: `Você está prestes a remover o item <b>${item.name}</b>.<br/><span class="text-xs text-slate-400">Se houver movimentações registradas, o histórico também poderá ser apagado ou bloqueado pelo banco.</span>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ef4444',
      background: '#0f172a',
      color: '#f8fafc'
    });

    if (!confirm.isConfirmed) return;

    try {
      await deleteStoreInventoryItem(item.id);
      setItems(prev => prev.filter(i => i.id !== item.id));
      showNotification('Item excluído com sucesso!', 'success');
    } catch (err: any) {
      console.error('Error deleting item:', err);
      showNotification('Não foi possível excluir o item. Considere apenas desativá-lo.', 'error');
    }
  };

  // Helper formatters
  const formatQuantity = (val: number, unit = 'un') => {
    const num = Number(val) || 0;
    // Se for inteiro, não exibe casas decimais
    const formatted = num % 1 === 0 ? num.toLocaleString('pt-BR') : num.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 3 });
    return `${formatted} ${unit}`;
  };

  const formatCurrency = (val: number | null) => {
    if (val === null || val === undefined) return '—';
    return Number(val).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const getStatusBadge = (item: StoreInventoryItem) => {
    if (!item.active) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-slate-800 text-slate-400 border border-slate-700">
          <i className="fas fa-ban text-[9px]"></i> Inativo
        </span>
      );
    }
    if (item.quantity <= 0) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-rose-500/15 text-rose-400 border border-rose-500/30">
          <i className="fas fa-triangle-exclamation text-[9px]"></i> Zerado
        </span>
      );
    }
    if (item.minimum_quantity !== null && item.quantity <= item.minimum_quantity) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500/15 text-amber-400 border border-amber-500/30 animate-pulse">
          <i className="fas fa-bell text-[9px]"></i> Estoque Baixo
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
        <i className="fas fa-circle-check text-[9px]"></i> Em Dia
      </span>
    );
  };

  const getMovementTypeBadge = (type: InventoryMovementType) => {
    switch (type) {
      case 'entry':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
            <i className="fas fa-arrow-down text-[9px]"></i> Entrada
          </span>
        );
      case 'consumption':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-rose-500/20 text-rose-400 border border-rose-500/30">
            <i className="fas fa-arrow-up text-[9px]"></i> Consumo
          </span>
        );
      case 'adjustment_in':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
            <i className="fas fa-plus text-[9px]"></i> Ajuste (+)
          </span>
        );
      case 'adjustment_out':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500/20 text-amber-400 border border-amber-500/30">
            <i className="fas fa-minus text-[9px]"></i> Ajuste (-)
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-slate-800 text-slate-300">
            {type}
          </span>
        );
    }
  };

  // Check auth & store
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <div className="w-12 h-12 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin"></div>
        <p className="text-slate-400 text-xs font-bold uppercase tracking-widest animate-pulse">Carregando Almoxarifado...</p>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="max-w-2xl mx-auto py-16 px-4 text-center">
        <div className="w-20 h-20 bg-slate-900 border border-slate-800 rounded-3xl flex items-center justify-center text-slate-500 mx-auto mb-6">
          <i className="fas fa-store-slash text-3xl"></i>
        </div>
        <h2 className="text-2xl font-black text-white uppercase tracking-tight">Loja não encontrada</h2>
        <p className="text-sm text-slate-400 mt-2 max-w-md mx-auto">
          Você precisa estar associado a uma loja para gerenciar o inventário interno e almoxarifado.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white font-black text-xs uppercase tracking-widest px-6 py-3 rounded-xl transition-all"
        >
          <i className="fas fa-arrow-left text-xs"></i> Voltar ao Início
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-16">
      {/* Top Header */}
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-black text-purple-400 uppercase tracking-widest bg-purple-500/10 px-2.5 py-0.5 rounded-full border border-purple-500/20">
              {store.name}
            </span>
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
              Almoxarifado & Materiais
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight uppercase flex items-center gap-3">
            <span>Inventário Interno</span>
          </h1>
          <p className="text-slate-400 text-xs mt-1">
            Controle de insumos, embalagens de pedidos, premiações de torneios e consumo diário.
          </p>
        </div>

        <div className="flex items-center flex-wrap gap-2.5">
          <button
            type="button"
            onClick={() => loadData(true)}
            disabled={refreshing}
            className="bg-slate-900/60 hover:bg-slate-800 text-slate-300 hover:text-white px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider border border-slate-800 transition-all flex items-center gap-2"
            title="Atualizar dados"
          >
            <i className={`fas fa-rotate text-xs ${refreshing ? 'animate-spin text-purple-400' : ''}`}></i>
            <span className="hidden sm:inline">Atualizar</span>
          </button>

          <button
            type="button"
            onClick={() => handleOpenHistory()}
            className="bg-slate-900/60 hover:bg-slate-800 text-slate-200 hover:text-white px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider border border-slate-800 transition-all flex items-center gap-2 hover:border-slate-700"
          >
            <i className="fas fa-clock-rotate-left text-xs text-purple-400"></i>
            <span>Extrato Geral</span>
          </button>

          <button
            type="button"
            onClick={() => setIsPredefinedModalOpen(true)}
            className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider transition-all shadow-lg shadow-emerald-600/20 active:scale-95 flex items-center gap-2"
            title="Adicionar itens com base em arquivo/catálogo pré-definido"
          >
            <i className="fas fa-boxes-packing text-xs text-emerald-100"></i>
            <span>Itens Pré-definidos</span>
            <span className="bg-emerald-700/90 text-[9px] px-1.5 py-0.5 rounded-full font-black text-emerald-100 uppercase tracking-wider">
              50+ Modelos
            </span>
          </button>

          <button
            type="button"
            onClick={handleOpenCreateModal}
            className="bg-purple-600 hover:bg-purple-500 text-white px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-purple-600/20 active:scale-95 flex items-center gap-2"
          >
            <i className="fas fa-plus text-xs"></i>
            <span>Novo Item</span>
          </button>
        </div>
      </header>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Total Itens */}
        <div 
          onClick={() => { setStatusFilter('all'); }}
          className={`bg-slate-900/50 border ${statusFilter === 'all' ? 'border-purple-500/50 ring-1 ring-purple-500/30' : 'border-slate-800'} p-5 rounded-2xl cursor-pointer hover:bg-slate-900/80 transition-all`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Itens Ativos</span>
            <div className="w-8 h-8 rounded-xl bg-purple-500/10 flex items-center justify-center text-purple-400">
              <i className="fas fa-boxes-stacked text-xs"></i>
            </div>
          </div>
          <div className="text-2xl font-black text-white">{stats.total}</div>
          <div className="text-[11px] text-slate-500 mt-1">Cadastrados no sistema</div>
        </div>

        {/* Estoque Baixo */}
        <div 
          onClick={() => { setStatusFilter(statusFilter === 'low_stock' ? 'all' : 'low_stock'); }}
          className={`bg-slate-900/50 border ${statusFilter === 'low_stock' ? 'border-amber-500/50 ring-1 ring-amber-500/30' : 'border-slate-800'} p-5 rounded-2xl cursor-pointer hover:bg-slate-900/80 transition-all`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-amber-400 uppercase tracking-widest">Atenção / Baixo</span>
            <div className="w-8 h-8 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-400">
              <i className="fas fa-triangle-exclamation text-xs"></i>
            </div>
          </div>
          <div className="text-2xl font-black text-amber-400">{stats.lowStock}</div>
          <div className="text-[11px] text-slate-500 mt-1">Abaixo do mínimo recomendado</div>
        </div>

        {/* Itens Zerados */}
        <div 
          onClick={() => { setStatusFilter(statusFilter === 'out_of_stock' ? 'all' : 'out_of_stock'); }}
          className={`bg-slate-900/50 border ${statusFilter === 'out_of_stock' ? 'border-rose-500/50 ring-1 ring-rose-500/30' : 'border-slate-800'} p-5 rounded-2xl cursor-pointer hover:bg-slate-900/80 transition-all`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-rose-400 uppercase tracking-widest">Zerados</span>
            <div className="w-8 h-8 rounded-xl bg-rose-500/10 flex items-center justify-center text-rose-400">
              <i className="fas fa-circle-xmark text-xs"></i>
            </div>
          </div>
          <div className="text-2xl font-black text-rose-400">{stats.outOfStock}</div>
          <div className="text-[11px] text-slate-500 mt-1">Necessitam de reposição urgente</div>
        </div>

        {/* Valor Total do Inventário */}
        <div className="bg-slate-900/50 border border-slate-800 p-5 rounded-2xl">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-emerald-400 uppercase tracking-widest">Valor em Estoque</span>
            <div className="w-8 h-8 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-400">
              <i className="fas fa-sack-dollar text-xs"></i>
            </div>
          </div>
          <div className="text-2xl font-black text-white">{formatCurrency(stats.totalVal)}</div>
          <div className="text-[11px] text-slate-500 mt-1">Com base no custo cadastrado</div>
        </div>
      </div>

      {/* Filters, Search and Sorting Bar */}
      <div className="bg-slate-900/60 border border-slate-800 p-4 rounded-2xl flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
        {/* Search */}
        <div className="relative flex-1 min-w-[240px]">
          <i className="fas fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, descrição ou categoria..."
            className="w-full pl-9 pr-4 py-2 bg-slate-950/80 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-xs"
            >
              <i className="fas fa-xmark"></i>
            </button>
          )}
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="bg-slate-950/80 border border-slate-800 rounded-xl text-xs text-slate-200 px-3 py-2 focus:outline-none focus:border-purple-500 cursor-pointer"
          >
            <option value="all">Todas as Categorias</option>
            {existingCategories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e: any) => setStatusFilter(e.target.value)}
            className="bg-slate-950/80 border border-slate-800 rounded-xl text-xs text-slate-200 px-3 py-2 focus:outline-none focus:border-purple-500 cursor-pointer"
          >
            <option value="all">Todos os Status</option>
            <option value="normal">Estoque em Dia</option>
            <option value="low_stock">Estoque Baixo</option>
            <option value="out_of_stock">Estoque Zerado</option>
            <option value="inactive">Itens Inativos</option>
          </select>

          {/* Sorting */}
          <select
            value={sortBy}
            onChange={(e: any) => setSortBy(e.target.value)}
            className="bg-slate-950/80 border border-slate-800 rounded-xl text-xs text-slate-200 px-3 py-2 focus:outline-none focus:border-purple-500 cursor-pointer"
          >
            <option value="name_asc">Nome (A - Z)</option>
            <option value="qty_asc">Menor Quantidade</option>
            <option value="qty_desc">Maior Quantidade</option>
            <option value="cost_desc">Maior Custo Unitário</option>
            <option value="val_desc">Maior Valor Total</option>
          </select>
        </div>
      </div>

      {/* Main Items Table / Cards */}
      {filteredItems.length === 0 ? (
        <div className="bg-slate-900/40 border border-slate-800 rounded-3xl p-12 text-center">
          <div className="w-16 h-16 bg-slate-800/60 rounded-2xl flex items-center justify-center text-slate-500 mx-auto mb-4">
            <i className="fas fa-boxes-packing text-2xl"></i>
          </div>
          <h3 className="text-base font-black text-white uppercase tracking-tight">Nenhum item encontrado</h3>
          <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
            {search || selectedCategory !== 'all' || statusFilter !== 'all'
              ? 'Tente ajustar os filtros ou termo de busca acima.'
              : 'Comece adicionando seu primeiro item de almoxarifado ou insumo da loja.'}
          </p>
          {(search || selectedCategory !== 'all' || statusFilter !== 'all') ? (
            <button
              onClick={() => { setSearch(''); setSelectedCategory('all'); setStatusFilter('all'); }}
              className="mt-4 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-xs font-bold text-white rounded-xl transition-all"
            >
              Limpar Filtros
            </button>
          ) : (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-5">
              <button
                type="button"
                onClick={() => setIsPredefinedModalOpen(true)}
                className="w-full sm:w-auto px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-xs font-black text-white uppercase tracking-wider rounded-xl transition-all shadow-lg shadow-emerald-600/20 flex items-center justify-center gap-2"
              >
                <i className="fas fa-boxes-packing text-xs text-emerald-200"></i>
                <span>Explorar Itens Pré-definidos</span>
              </button>
              <button
                type="button"
                onClick={handleOpenCreateModal}
                className="w-full sm:w-auto px-5 py-2.5 bg-slate-800 hover:bg-slate-700 text-xs font-bold text-white uppercase tracking-wider rounded-xl transition-all border border-slate-700 flex items-center justify-center gap-2"
              >
                <i className="fas fa-plus text-xs"></i>
                <span>Adicionar Manualmente</span>
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-slate-900/40 border border-slate-800 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-800/80 bg-slate-950/40 text-[10px] font-black text-slate-400 uppercase tracking-wider">
                  <th className="py-3.5 px-4">Item & Categoria</th>
                  <th className="py-3.5 px-4 text-center">Saldo Atual</th>
                  <th className="py-3.5 px-4 text-center">Estoque Mínimo</th>
                  <th className="py-3.5 px-4 text-center">Status</th>
                  <th className="py-3.5 px-4 text-right">Custo Unitário</th>
                  <th className="py-3.5 px-4 text-right">Valor Total</th>
                  <th className="py-3.5 px-4 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {filteredItems.map((item) => {
                  const isLow = item.active && item.minimum_quantity !== null && item.quantity <= item.minimum_quantity;
                  const isZero = item.active && item.quantity <= 0;
                  const totalItemVal = item.quantity * (item.cost || 0);

                  return (
                    <tr 
                      key={item.id} 
                      className={`hover:bg-slate-800/30 transition-colors ${
                        isZero ? 'bg-rose-950/10' : isLow ? 'bg-amber-950/10' : ''
                      }`}
                    >
                      {/* Nome e Descrição */}
                      <td className="py-4 px-4">
                        <div className="flex items-start gap-3">
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
                            isZero 
                              ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20' 
                              : isLow 
                                ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' 
                                : 'bg-slate-800/80 text-slate-300 border border-slate-700/50'
                          }`}>
                            <i className="fas fa-box text-xs"></i>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-white tracking-tight">
                                {item.name}
                              </span>
                              {item.category && (
                                <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-slate-800 text-slate-400 border border-slate-700/50">
                                  {item.category}
                                </span>
                              )}
                            </div>
                            {item.description && (
                              <p className="text-[11px] text-slate-400 line-clamp-1 mt-0.5">
                                {item.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Saldo Atual */}
                      <td className="py-4 px-4 text-center">
                        <div className="inline-flex flex-col items-center">
                          <span className={`text-base font-black ${
                            isZero ? 'text-rose-400' : isLow ? 'text-amber-400' : 'text-white'
                          }`}>
                            {formatQuantity(item.quantity, item.unit)}
                          </span>
                        </div>
                      </td>

                      {/* Estoque Mínimo */}
                      <td className="py-4 px-4 text-center text-xs text-slate-400">
                        {item.minimum_quantity !== null && item.minimum_quantity !== undefined ? (
                          <span className="font-semibold text-slate-300">
                            {formatQuantity(item.minimum_quantity, item.unit)}
                          </span>
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="py-4 px-4 text-center">
                        {getStatusBadge(item)}
                      </td>

                      {/* Custo Unitário */}
                      <td className="py-4 px-4 text-right text-xs font-semibold text-slate-300">
                        {formatCurrency(item.cost)}
                      </td>

                      {/* Valor Total */}
                      <td className="py-4 px-4 text-right text-xs font-bold text-white">
                        {formatCurrency(totalItemVal)}
                      </td>

                      {/* Ações */}
                      <td className="py-4 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Botão Movimentar */}
                          <button
                            type="button"
                            onClick={() => handleOpenMovementModal(item)}
                            className="bg-purple-600/20 hover:bg-purple-600 text-purple-300 hover:text-white px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all border border-purple-500/30 flex items-center gap-1"
                            title="Lançar movimentação de estoque (Entrada, Consumo, Ajuste)"
                          >
                            <i className="fas fa-right-left text-[10px]"></i>
                            <span className="hidden sm:inline">Movimentar</span>
                          </button>

                          {/* Botão Histórico */}
                          <button
                            type="button"
                            onClick={() => handleOpenHistory(item)}
                            className="bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white p-1.5 rounded-lg text-xs transition-all border border-slate-700"
                            title="Histórico deste item"
                          >
                            <i className="fas fa-clock-rotate-left"></i>
                          </button>

                          {/* Botão Editar */}
                          <button
                            type="button"
                            onClick={() => handleOpenEditModal(item)}
                            className="bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white p-1.5 rounded-lg text-xs transition-all border border-slate-700"
                            title="Editar dados cadastrais"
                          >
                            <i className="fas fa-pencil"></i>
                          </button>

                          {/* Botão Desativar / Reativar */}
                          <button
                            type="button"
                            onClick={() => handleToggleActive(item)}
                            className={`p-1.5 rounded-lg text-xs transition-all border ${
                              item.active 
                                ? 'bg-slate-800 hover:bg-rose-500/20 text-slate-400 hover:text-rose-400 border-slate-700 hover:border-rose-500/30' 
                                : 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border-emerald-500/40'
                            }`}
                            title={item.active ? 'Desativar item' : 'Reativar item'}
                          >
                            <i className={`fas ${item.active ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                          </button>

                          {/* Botão Excluir */}
                          <button
                            type="button"
                            onClick={() => handleDeleteItem(item)}
                            className="bg-slate-800 hover:bg-rose-900/40 text-slate-500 hover:text-rose-400 p-1.5 rounded-lg text-xs transition-all border border-slate-700 hover:border-rose-800/50"
                            title="Excluir item definitivamente"
                          >
                            <i className="fas fa-trash-can"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MODAL: Criar / Editar Item */}
      {isItemModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-2xl p-6 md:p-8 space-y-6">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div>
                <h3 className="text-xl font-black text-white uppercase tracking-tight">
                  {editingItem ? 'Editar Item do Almoxarifado' : 'Novo Item do Almoxarifado'}
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  {editingItem ? 'Atualize as informações cadastrais do item.' : 'Cadastre um novo material, embalagem ou insumo interno.'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsItemModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
              >
                <i className="fas fa-xmark"></i>
              </button>
            </div>

            <form onSubmit={handleSaveItem} className="space-y-4">
              {/* Preenchimento rápido a partir de modelo pré-definido */}
              {!editingItem && (
                <div className="p-3 bg-slate-950/80 border border-slate-800 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                  <div className="flex items-center gap-2">
                    <i className="fas fa-wand-magic-sparkles text-emerald-400 text-xs shrink-0"></i>
                    <div>
                      <span className="text-xs font-bold text-white block">Preencher com Modelo Pré-definido</span>
                      <span className="text-[10px] text-slate-400">Auto-preenche nome, categoria, unidade e custo sugerido</span>
                    </div>
                  </div>
                  <select
                    defaultValue=""
                    onChange={(e) => {
                      const found = PREDEFINED_INVENTORY_ITEMS.find(p => p.id === e.target.value);
                      if (found) {
                        setItemFormData(prev => ({
                          ...prev,
                          name: found.name,
                          category: found.category,
                          unit: found.unit,
                          description: found.description,
                          minimum_quantity: found.suggested_minimum_quantity ? String(found.suggested_minimum_quantity) : '',
                          cost: found.suggested_cost !== undefined ? String(found.suggested_cost) : '',
                        }));
                      }
                    }}
                    className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-1.5 text-xs text-purple-300 font-bold focus:outline-none focus:border-purple-500 cursor-pointer sm:max-w-[220px]"
                  >
                    <option value="" disabled>Escolher modelo (50+)...</option>
                    {PREDEFINED_INVENTORY_ITEMS.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Nome */}
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                  Nome do Item <span className="text-purple-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={itemFormData.name}
                  onChange={(e) => setItemFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Envelope Bolha 15x20cm, Sleeves Standard, Bobina Térmica..."
                  className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                />
              </div>

              {/* Categoria e Unidade */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                    Categoria
                  </label>
                  <input
                    type="text"
                    list="categories-list"
                    value={itemFormData.category}
                    onChange={(e) => setItemFormData(prev => ({ ...prev, category: e.target.value }))}
                    placeholder="Selecione ou digite..."
                    className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                  />
                  <datalist id="categories-list">
                    {PRESET_CATEGORIES.map(c => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                    Unidade de Medida <span className="text-purple-400">*</span>
                  </label>
                  <select
                    value={itemFormData.unit}
                    onChange={(e) => setItemFormData(prev => ({ ...prev, unit: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white focus:outline-none focus:border-purple-500 cursor-pointer"
                  >
                    {COMMON_UNITS.map(u => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Quantidade Inicial (apenas criação) */}
              {!editingItem && (
                <div className="p-4 bg-purple-950/20 border border-purple-500/20 rounded-2xl space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-black text-purple-300 uppercase tracking-wider">
                      Estoque Inicial
                    </label>
                    <span className="text-[10px] text-purple-400">Registrado como primeira movimentação</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-1">
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={itemFormData.quantity}
                        onChange={(e) => setItemFormData(prev => ({ ...prev, quantity: e.target.value }))}
                        placeholder="0"
                        className="w-full px-4 py-2 bg-slate-950 border border-purple-500/30 rounded-xl text-xs text-white font-black focus:outline-none focus:border-purple-400"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <input
                        type="text"
                        value={itemFormData.initial_reason}
                        onChange={(e) => setItemFormData(prev => ({ ...prev, initial_reason: e.target.value }))}
                        placeholder="Motivo (ex: Estoque inicial)"
                        className="w-full px-4 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Estoque Mínimo e Custo Unitário */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                    Estoque Mínimo (Alerta)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={itemFormData.minimum_quantity}
                    onChange={(e) => setItemFormData(prev => ({ ...prev, minimum_quantity: e.target.value }))}
                    placeholder="Ex: 10 (dispara alerta de reposição)"
                    className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                  />
                  <span className="text-[10px] text-slate-500 mt-1 block">
                    Alerta quando o saldo for igual ou menor a esse valor.
                  </span>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                    Custo Unitário Médio (R$)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={itemFormData.cost}
                    onChange={(e) => setItemFormData(prev => ({ ...prev, cost: e.target.value }))}
                    placeholder="Ex: 2.50"
                    className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                  />
                  <span className="text-[10px] text-slate-500 mt-1 block">
                    Usado para estimar o valor total estocado.
                  </span>
                </div>
              </div>

              {/* Descrição / Notas */}
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                  Descrição / Observações (Opcional)
                </label>
                <textarea
                  rows={2}
                  value={itemFormData.description}
                  onChange={(e) => setItemFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Detalhes, especificações ou fornecedor de preferência..."
                  className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* Status Ativo */}
              <div className="flex items-center gap-3 pt-2">
                <input
                  type="checkbox"
                  id="item-active-check"
                  checked={itemFormData.active}
                  onChange={(e) => setItemFormData(prev => ({ ...prev, active: e.target.checked }))}
                  className="w-4 h-4 rounded text-purple-600 bg-slate-950 border-slate-700 focus:ring-purple-500"
                />
                <label htmlFor="item-active-check" className="text-xs font-bold text-slate-300 cursor-pointer">
                  Item ativo no almoxarifado
                </label>
              </div>

              {/* Botões do Formulário */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsItemModalOpen(false)}
                  className="px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingItem}
                  className="px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest text-white bg-purple-600 hover:bg-purple-500 transition-all shadow-lg shadow-purple-600/20 disabled:opacity-50 flex items-center gap-2"
                >
                  {savingItem && <i className="fas fa-spinner animate-spin"></i>}
                  <span>{editingItem ? 'Salvar Alterações' : 'Cadastrar Item'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Registrar Movimentação de Estoque (RPC) */}
      {isMovementModalOpen && movementItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl p-6 md:p-8 space-y-6">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div>
                <span className="text-[10px] font-black text-purple-400 uppercase tracking-widest">
                  Lançamento de Estoque
                </span>
                <h3 className="text-xl font-black text-white tracking-tight">
                  {movementItem.name}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsMovementModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
              >
                <i className="fas fa-xmark"></i>
              </button>
            </div>

            {/* Saldo Atual Display */}
            <div className="bg-slate-950/80 border border-slate-800 p-4 rounded-2xl flex items-center justify-between">
              <div>
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block">Saldo Atual</span>
                <span className="text-xl font-black text-white">
                  {formatQuantity(movementItem.quantity, movementItem.unit)}
                </span>
              </div>
              {movementItem.minimum_quantity !== null && (
                <div className="text-right">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block">Mínimo</span>
                  <span className="text-xs font-bold text-slate-400">
                    {formatQuantity(movementItem.minimum_quantity, movementItem.unit)}
                  </span>
                </div>
              )}
            </div>

            <form onSubmit={handleExecuteMovement} className="space-y-5">
              {/* Tipo de Movimentação */}
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-2">
                  Tipo de Movimentação
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {/* Entrada */}
                  <button
                    type="button"
                    onClick={() => { setMovementType('entry'); setMovementReason(''); }}
                    className={`p-3 rounded-xl border text-left flex items-center gap-3 transition-all ${
                      movementType === 'entry'
                        ? 'bg-emerald-500/15 border-emerald-500 text-white ring-1 ring-emerald-500/30'
                        : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                      <i className="fas fa-arrow-down text-xs"></i>
                    </div>
                    <div>
                      <div className="text-xs font-black text-white">Entrada (+)</div>
                      <div className="text-[10px] text-slate-400">Compra / Reposição</div>
                    </div>
                  </button>

                  {/* Consumo */}
                  <button
                    type="button"
                    onClick={() => { setMovementType('consumption'); setMovementReason(''); }}
                    className={`p-3 rounded-xl border text-left flex items-center gap-3 transition-all ${
                      movementType === 'consumption'
                        ? 'bg-rose-500/15 border-rose-500 text-white ring-1 ring-rose-500/30'
                        : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-rose-500/20 text-rose-400 flex items-center justify-center shrink-0">
                      <i className="fas fa-arrow-up text-xs"></i>
                    </div>
                    <div>
                      <div className="text-xs font-black text-white">Consumo (-)</div>
                      <div className="text-[10px] text-slate-400">Torneio / Balcão</div>
                    </div>
                  </button>

                  {/* Ajuste Positivo */}
                  <button
                    type="button"
                    onClick={() => { setMovementType('adjustment_in'); setMovementReason(''); }}
                    className={`p-3 rounded-xl border text-left flex items-center gap-3 transition-all ${
                      movementType === 'adjustment_in'
                        ? 'bg-cyan-500/15 border-cyan-500 text-white ring-1 ring-cyan-500/30'
                        : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-cyan-500/20 text-cyan-400 flex items-center justify-center shrink-0">
                      <i className="fas fa-plus text-xs"></i>
                    </div>
                    <div>
                      <div className="text-xs font-black text-white">Ajuste (+)</div>
                      <div className="text-[10px] text-slate-400">Contagem física</div>
                    </div>
                  </button>

                  {/* Ajuste Negativo */}
                  <button
                    type="button"
                    onClick={() => { setMovementType('adjustment_out'); setMovementReason(''); }}
                    className={`p-3 rounded-xl border text-left flex items-center gap-3 transition-all ${
                      movementType === 'adjustment_out'
                        ? 'bg-amber-500/15 border-amber-500 text-white ring-1 ring-amber-500/30'
                        : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0">
                      <i className="fas fa-minus text-xs"></i>
                    </div>
                    <div>
                      <div className="text-xs font-black text-white">Ajuste (-)</div>
                      <div className="text-[10px] text-slate-400">Avaria / Perda</div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Quantidade a Movimentar */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider">
                    Quantidade a Movimentar ({movementItem.unit})
                  </label>
                  {movementPreview && (
                    <span className={`text-xs font-bold ${
                      movementPreview.isNegative ? 'text-rose-400 font-black' : 'text-slate-300'
                    }`}>
                      Novo Saldo: {formatQuantity(movementPreview.next, movementItem.unit)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0.001"
                    step="any"
                    required
                    value={movementQuantity}
                    onChange={(e) => setMovementQuantity(e.target.value)}
                    className="flex-1 px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-base font-black text-white focus:outline-none focus:border-purple-500"
                  />
                  <div className="flex items-center gap-1">
                    {[1, 5, 10, 50].map(val => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setMovementQuantity(String(val))}
                        className="px-2.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-lg transition-colors"
                      >
                        +{val}
                      </button>
                    ))}
                  </div>
                </div>
                {movementPreview?.isNegative && (
                  <p className="text-[11px] text-rose-400 font-bold mt-1.5 flex items-center gap-1">
                    <i className="fas fa-triangle-exclamation"></i>
                    Atenção: A quantidade informada excede o saldo disponível em estoque!
                  </p>
                )}
              </div>

              {/* Motivo (Reason) */}
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                  Motivo da Movimentação
                </label>
                <input
                  type="text"
                  value={movementReason}
                  onChange={(e) => setMovementReason(e.target.value)}
                  placeholder="Ex: Torneio de Sábado, Reposição de sleeves..."
                  className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 mb-2"
                />
                {/* Sugestões de motivos */}
                <div className="flex flex-wrap gap-1.5">
                  {REASON_SUGGESTIONS[movementType].map(sug => (
                    <button
                      key={sug}
                      type="button"
                      onClick={() => setMovementReason(sug)}
                      className="px-2.5 py-1 bg-slate-950 hover:bg-slate-800 border border-slate-800 text-[11px] text-slate-400 hover:text-white rounded-lg transition-colors"
                    >
                      {sug}
                    </button>
                  ))}
                </div>
              </div>

              {/* Observações Opcionais */}
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">
                  Observações Adicionais (Opcional)
                </label>
                <textarea
                  rows={2}
                  value={movementNotes}
                  onChange={(e) => setMovementNotes(e.target.value)}
                  placeholder="Detalhes adicionais, número de nota fiscal, mesa ou evento..."
                  className="w-full px-4 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* Ações */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsMovementModalOpen(false)}
                  className="px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingMovement || movementPreview?.isNegative}
                  className="px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest text-white bg-purple-600 hover:bg-purple-500 transition-all shadow-lg shadow-purple-600/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {savingMovement && <i className="fas fa-spinner animate-spin"></i>}
                  <span>Confirmar Lançamento</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL / DRAWER: Histórico de Movimentações */}
      {isHistoryModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4 shrink-0">
              <div>
                <span className="text-[10px] font-black text-purple-400 uppercase tracking-widest">
                  Registro de Auditoria
                </span>
                <h3 className="text-xl font-black text-white tracking-tight">
                  {historyItem ? `Histórico: ${historyItem.name}` : 'Histórico Geral de Movimentações'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsHistoryModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
              >
                <i className="fas fa-xmark"></i>
              </button>
            </div>

            {/* Content List */}
            <div className="flex-1 overflow-y-auto space-y-3 pr-1">
              {loadingMovements ? (
                <div className="py-16 text-center space-y-3">
                  <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">Carregando movimentações...</p>
                </div>
              ) : movements.length === 0 ? (
                <div className="py-16 text-center text-slate-500 italic text-sm">
                  Nenhuma movimentação registrada até o momento.
                </div>
              ) : (
                movements.map((mov) => (
                  <div
                    key={mov.id}
                    className="p-4 bg-slate-950/80 border border-slate-800 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:border-slate-700 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 shrink-0">
                        {getMovementTypeBadge(mov.movement_type)}
                      </div>
                      <div>
                        {!historyItem && mov.item && (
                          <div className="text-xs font-bold text-white mb-0.5">
                            {mov.item.name}
                          </div>
                        )}
                        <p className="text-xs text-slate-300 font-medium">
                          {mov.reason || 'Sem motivo especificado'}
                        </p>
                        {mov.notes && (
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            {mov.notes}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="text-left sm:text-right shrink-0">
                      <div className="text-sm font-black text-white">
                        {mov.movement_type === 'entry' || mov.movement_type === 'adjustment_in' ? '+' : '-'}
                        {mov.quantity} {mov.item?.unit || historyItem?.unit || 'un'}
                      </div>
                      <div className="text-[10px] text-slate-500 font-semibold mt-0.5">
                        {new Date(mov.created_at).toLocaleString('pt-BR')}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="pt-3 border-t border-slate-800 flex justify-end shrink-0">
              <button
                type="button"
                onClick={() => setIsHistoryModalOpen(false)}
                className="px-5 py-2.5 bg-slate-800 hover:bg-slate-700 text-xs font-bold text-white rounded-xl transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Itens Pré-definidos do Almoxarifado */}
      {store && (
        <PredefinedInventoryModal
          isOpen={isPredefinedModalOpen}
          onClose={() => setIsPredefinedModalOpen(false)}
          storeId={store.id}
          existingItems={items}
          onItemsAdded={(newItems) => {
            setItems(prev => [...newItems, ...prev]);
          }}
        />
      )}
    </div>
  );
};
